import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell,
  ComposedChart,
  Line
} from "recharts";
import { Users, TrendingUp, AlertTriangle, Euro } from "lucide-react";

const RISK_COLORS = {
  verde: "#22c55e",
  medio: "#eab308",
  forte: "#f97316",
  molto_forte: "#ef4444"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const AnalyticsTC = ({ kpi, preventivi }) => {
  // Prepare TC data
  const tcData = useMemo(() => {
    if (!kpi?.by_tc) return [];
    
    return Object.entries(kpi.by_tc)
      .map(([tc, data]) => {
        const total = data.count;
        const riskScore = total > 0 ? 
          ((data.risk.medio * 1 + data.risk.forte * 2 + data.risk.molto_forte * 3) / total).toFixed(2) : 0;
        
        return {
          name: tc.length > 20 ? tc.substring(0, 20) + "..." : tc,
          fullName: tc,
          preventivi: data.count,
          valore: data.value,
          valoreMila: data.value / 1000,
          avgValue: data.count > 0 ? data.value / data.count : 0,
          verde: data.risk.verde,
          medio: data.risk.medio,
          forte: data.risk.forte,
          molto_forte: data.risk.molto_forte,
          riskScore: parseFloat(riskScore),
          riskPercent: total > 0 ? ((data.risk.forte + data.risk.molto_forte) / total * 100).toFixed(1) : 0
        };
      })
      .sort((a, b) => b.valore - a.valore);
  }, [kpi]);

  // Top performers
  const topByValue = [...tcData].sort((a, b) => b.valore - a.valore).slice(0, 5);
  const topByCount = [...tcData].sort((a, b) => b.preventivi - a.preventivi).slice(0, 5);
  const highestRisk = [...tcData].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);

  if (!kpi || tcData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Nessun dato disponibile
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-tc-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
          Performance Tecnici Commerciali
        </h2>
        <Badge variant="outline" className="text-slate-600">
          {tcData.length} TC Analizzati
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Users className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">TC Totali</p>
                <p className="text-xl font-bold font-heading">{tcData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Euro className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Media per TC</p>
                <p className="text-xl font-bold font-heading">
                  {formatCurrency(kpi.total_value / tcData.length)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Top Performer</p>
                <p className="text-lg font-bold font-heading truncate max-w-[150px]" title={topByValue[0]?.fullName}>
                  {topByValue[0]?.name || "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Rischio Più Alto</p>
                <p className="text-lg font-bold font-heading truncate max-w-[150px]" title={highestRisk[0]?.fullName}>
                  {highestRisk[0]?.name || "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Value by TC */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Valore per TC (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={tcData.slice(0, 10)} 
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), "Valore"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Bar dataKey="valore" fill="#f97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Risk Distribution by TC */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Distribuzione Rischio per TC (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={tcData.slice(0, 10)} 
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Legend />
                  <Bar dataKey="verde" stackId="a" fill={RISK_COLORS.verde} name="Verde" />
                  <Bar dataKey="medio" stackId="a" fill={RISK_COLORS.medio} name="Medio" />
                  <Bar dataKey="forte" stackId="a" fill={RISK_COLORS.forte} name="Forte" />
                  <Bar dataKey="molto_forte" stackId="a" fill={RISK_COLORS.molto_forte} name="Molto Forte" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance vs Risk Chart */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Performance vs Rischio (Valore e % Rischio Alto)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={tcData.slice(0, 15)} margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                <Tooltip 
                  formatter={(value, name) => [
                    name === "valore" ? formatCurrency(value) : `${value}%`,
                    name === "valore" ? "Valore" : "% Rischio Alto"
                  ]}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="valore" fill="#0f172a" name="Valore" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="riskPercent" stroke="#ef4444" strokeWidth={2} name="% Rischio Alto" dot={{ fill: "#ef4444" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* TC Detail Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Dettaglio Completo TC
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tc-detail-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">TC</th>
                  <th className="text-center py-3 px-4 font-semibold">Preventivi</th>
                  <th className="text-right py-3 px-4 font-semibold">Valore Totale</th>
                  <th className="text-right py-3 px-4 font-semibold">Media</th>
                  <th className="text-center py-3 px-4 font-semibold text-green-600">Verde</th>
                  <th className="text-center py-3 px-4 font-semibold text-yellow-600">Medio</th>
                  <th className="text-center py-3 px-4 font-semibold text-orange-600">Forte</th>
                  <th className="text-center py-3 px-4 font-semibold text-red-600">M. Forte</th>
                  <th className="text-center py-3 px-4 font-semibold">% Rischio</th>
                </tr>
              </thead>
              <tbody>
                {tcData.map((tc, i) => (
                  <tr key={tc.fullName} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium" title={tc.fullName}>{tc.name}</td>
                    <td className="py-3 px-4 text-center">{tc.preventivi}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(tc.valore)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(tc.avgValue)}</td>
                    <td className="py-3 px-4 text-center text-green-600">{tc.verde}</td>
                    <td className="py-3 px-4 text-center text-yellow-600">{tc.medio}</td>
                    <td className="py-3 px-4 text-center text-orange-600">{tc.forte}</td>
                    <td className="py-3 px-4 text-center text-red-600">{tc.molto_forte}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        parseFloat(tc.riskPercent) > 50 ? "bg-red-100 text-red-700" :
                        parseFloat(tc.riskPercent) > 25 ? "bg-orange-100 text-orange-700" :
                        "bg-green-100 text-green-700"
                      }`}>
                        {tc.riskPercent}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTC;
